import { useEffect, useRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import { WebTemplateContext } from "../context/WebTemplateContext";
import { useCookieConsent } from "../context/CookieConsentContext";

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";

export function useGoogleAnalytics() {
	const { webTemplate } = useContext(WebTemplateContext);
	const { thirdParty } = useCookieConsent();
	const location = useLocation();
	const initializedRef = useRef(null);
	const lastPathRef = useRef(null);

	const measurementId = webTemplate?.gaMeasurementId;
	const shouldLoad = Boolean(measurementId && thirdParty);

	useEffect(() => {
		if (!shouldLoad) {
			if (initializedRef.current) {
				removeGtagScript(initializedRef.current);
				initializedRef.current = null;
				lastPathRef.current = null;
			}
			return;
		}

		if (initializedRef.current === measurementId) return;

		if (initializedRef.current) {
			removeGtagScript(initializedRef.current);
		}

		const script = document.createElement("script");
		script.src = `${GTAG_SRC}?id=${measurementId}`;
		script.async = true;
		script.dataset.gaId = measurementId;
		document.head.appendChild(script);

		window.dataLayer = window.dataLayer || [];
		function gtag() { window.dataLayer.push(arguments); }
		window.gtag = gtag;
		gtag("js", new Date());
		gtag("config", measurementId, {
			page_path: location.pathname + location.search,
			send_page_view: true,
		});

		initializedRef.current = measurementId;
		lastPathRef.current = location.pathname + location.search;
	}, [shouldLoad, measurementId]);

	useEffect(() => {
		if (!initializedRef.current || !window.gtag) return;
		const currentPath = location.pathname + location.search;
		if (currentPath === lastPathRef.current) return;
		lastPathRef.current = currentPath;
		window.gtag("event", "page_view", {
			page_path: currentPath,
		});
	}, [location]);
}

function removeGtagScript(id) {
	const script = document.querySelector(`script[data-ga-id="${id}"]`);
	if (script) script.remove();

	// Clear GA cookies
	const cookies = document.cookie.split(";");
	for (const cookie of cookies) {
		const name = cookie.split("=")[0].trim();
		if (name.startsWith("_ga") || name.startsWith("_gid")) {
			document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
			document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
		}
	}

	window.dataLayer = [];
	delete window.gtag;
}
